import React, { useState, useEffect } from 'react'
import { Button, Form, Select } from 'antd'
import './style.less'
import { CloseCircleOutlined } from '@ant-design/icons'
import { offlineOptionList, onlineOptionList } from './option'
import RenderComponent from './RenderComponent'
import { AIQualityCheckCombinedFilterItem, CombinedFilterItemDataType } from 'src/types/ai-quality-check'
import moment from 'moment'
import { ActionMode } from '@pages/config/quality-check-scheme-detail/constants'

interface CombinedFilterProps {
  filterType?: 'create' | 'edit' | 'view',
  actionMode?: number,
  data?: AIQualityCheckCombinedFilterItem[],
  className?: string,
  exportFormValue?: (value, isValid) => void,
}

const CommonCombinedFilter = (props: CombinedFilterProps) => {
  const { filterType = 'edit', actionMode = ActionMode.offline, data, className, exportFormValue } = props
  const [form] = Form.useForm()
  const [conditionId, setConditionId] = useState(0)
  const [selectedKeyMap, setSelectedKeyMap] = useState({}) // 记录当前已选择类型，用于限制条件不可复选
  const [isInit, setIsInit] = useState(true)
  const optionList = actionMode === 1 ? offlineOptionList : onlineOptionList

  // 获取组件值，回显数据时进行特殊处理
  const getRangeValue = (item) => {
    const config = optionList.find(option => option?.value === item?.fieldName)
    const isCombined = config?.isCombined || false
    const isDuration = config?.isDuration || false
    const isArray = config?.isArray || false
    if (isArray) {
      // 级联选择类型，因为传给后端的值需要特殊处理，原来组件的值被存放到label中，回显数据时需要从label中取值
      return item?.label[1]
    }
    if (isDuration) {
      // 时间区间类型，回显数据时需要格式化为数组
      const op = item?.op
      if (Array.isArray(item?.value)) return item?.value
      return op === 'ge' ? [item?.value, null] : [null, item?.value]
    }
    return isCombined ? [item?.op, item?.value] : item?.value
  }
  // 处理级联选择类型label
  const getArrayLabels = (value, option) => {
    // 特殊处理级联选择类型，目的是暂存组件的值，使得数据可以正常回显
    return [option.flatMap(subArray => subArray?.length === 1 ? subArray?.[0]?.label : subArray?.slice(1).map(item => item?.label)), value]
  }

  // 根据path获取对象中的值
  const getLabelByPath = (option, path) => {
    const paths = path.split('.') || []
    return paths.reduce((obj, key) => {
      if (Array.isArray(obj)) {
        return obj.map(item => item?.[key])
      }
      return obj?.[key]
    }, option)
  }

  // 获取组件label，用于展示态时显示正确的标签
  const getItemLabel = (item, value, option) => {
    const config = optionList.find(opt => opt?.value === item?.value?.fieldName)
    const path = config?.labelPath || ''
    const isArray = config?.isArray || false
    const isTime = config?.isTime || false
    const isCombined = config?.isCombined || false
    const isDuration = config?.isDuration || false
    if (isCombined) {
      // 处理左符号右数值类型
      const currentLabel = item?.value?.label || []
      const newLabel = [...currentLabel]
      newLabel[1] = item?.value?.rangeValue?.[1]
      if (option !== undefined) {
        // 处理数值更改
        newLabel[0] = option?.label
      }
      if (item?.value?.rangeValue?.[0] === undefined) {
        // 处理符号被清空
        newLabel[0] = undefined
      }
      return newLabel
    }
    if (isDuration) {
      // 处理时间区间类型，时间区间需要支持单个值
      const realValues = item?.value?.rangeValue?.filter(val => val !== null && val !== undefined)
      return realValues.length === 1 ? realValues[0] : item?.value?.rangeValue
    }
    if (isTime) {
      // 处理时间类型，进行时间格式化处理
      return item?.value?.rangeValue?.map(value => value ? moment(value).format('YYYY-MM-DD') : '')
    }
    if (!path) return null
    return isArray ? getArrayLabels(value, option) : getLabelByPath(option, path)
  }

  useEffect(() => {
    if (!isInit) {
      form.resetFields()
      setConditionId(0)
      setSelectedKeyMap({})
    }
    setIsInit(false)
  }, [actionMode])

  // 初始化表单值
  useEffect(() => {
    if (data && data.length > 0) {
      const initialConditions = data.map((item, index) => {
        return {
          id: index,
          value: {
            fieldName: item.fieldName,
            rangeValue: getRangeValue(item),
            label: item.label,
          },
          op: item.op,
        }
      })
      const initialSelectedKeyMap = initialConditions.reduce((map, condition) => {
        map[condition?.id] = condition?.value?.fieldName
        return map
      }, {})
      form.setFieldsValue({ conditions: initialConditions })
      setSelectedKeyMap(initialSelectedKeyMap)
      setConditionId(data.length - 1)
    }
  }, [data])

  // 暴露表单数据并进行验证
  const handleFormValueChange = () => {
    form.validateFields().then(() => {
      exportFormValue && exportFormValue(form.getFieldValue('conditions'), true)
    }, () => {
      exportFormValue && exportFormValue(form.getFieldValue('conditions'), false)
    })
  }

  useEffect(() => {
    handleFormValueChange()
  }, [form.getFieldValue('conditions')])

  // 组件值校验
  const validateConditionValue = (currentCondition, value, config) => {
    // 处理通话时长类型，支持单个值
    if (config?.isDuration) {
      if (value?.some(item => item !== null && item !== undefined)) {
        return true
      }
      return false
    }
    // 处理新增时未选中类型的情况
    if (currentCondition?.value?.fieldName === undefined) return true
    if (!(currentCondition?.value?.fieldName !== undefined && value !== undefined)) {
      return false
    }
    if ((typeof value === 'string' && value.trim() !== '') || typeof value === 'number') {
      return true
    }
    if (Array.isArray(value) && value?.length > 0 && value?.every(item => item !== undefined && item !== null)) return true
    return false
  }

  // 格式化展示值，根据数据类型做不同的展示处理，例如添加秒数后缀
  const formatDisplayValue = (displayValue, config?: { isDuration?: boolean; isTime?: boolean }) => {
    if (displayValue === undefined || displayValue === null) return ''
    if (Array.isArray(displayValue)) {
      if (config?.isDuration) {
        return displayValue.map(value => `${value}s`).join(' ～ ')
      }
      return config?.isTime ? displayValue.join(' ～ ') : displayValue.join(', ')
    }

    return config?.isDuration ? `${displayValue}s` : String(displayValue)
  }

  // 获取当前条件的展示值
  const getDisplayValue = (currentCondition, config) => {
    if (config?.isArray) {
      // 级联数组类型label第一个值是前端展示label
      return currentCondition?.value?.label?.[0]
    }
    // 左符号右数值类型只需要读取值
    return config?.isCombined ? currentCondition?.value?.rangeValue?.[1] : (currentCondition?.value?.label || currentCondition?.value?.rangeValue || '')
  }

  return (
    <div className={`combined-filter-container ${className}`}>
      <Form
        form={form}
        wrapperCol={{ span: 24 }}
        initialValues={{ conditions: [{ id: 0, value: { fieldName: undefined, rangeValue: undefined, label: undefined } }] }}
      >
        <Form.List
          name="conditions"
        >
          {(fields, { add, remove }) => {
            return (
              <>
                {(filterType === 'create' || filterType === 'edit') && (
                  <Button type="primary" ghost className="ml10 mb10 combined-filter-button" onClick={() => {
                    add({ id: conditionId + 1, value: { fieldName: undefined, rangeValue: undefined, label: undefined } })
                    setConditionId(conditionId + 1)
                  }}>新增筛选
                  </Button>
                )}
                <div style={{ position: 'relative', borderLeft: fields.length > 1 ? '1px solid #0052D9' : 'none' }}>
                  {fields.map((field, index) => {
                    // 获取当前条件信息，用于展示等
                    const currentCondition = (form.getFieldValue('conditions') || []).find((item: CombinedFilterItemDataType) => item?.id === field?.key)
                    const config = currentCondition?.value?.fieldName ? optionList.find(opt => opt?.value === currentCondition?.value?.fieldName) : undefined
                    const label = config?.label || currentCondition?.value?.fieldName || ''
                    const opertor = config?.opLabel[currentCondition?.op] || ''
                    const displayValue = getDisplayValue(currentCondition, config)

                    return (
                      <div key={field.key} className="combined-filter-item">
                        <div className="combined-filter-content">
                          <div className="combined-filter-content-number">{index}</div>
                          <Form.Item
                            {...field}
                            style={{ marginBottom: 0 }}
                            rules={[
                              {
                                validator: (_, value: CombinedFilterItemDataType) => {
                                  if (filterType === 'view' || value?.value?.fieldName !== undefined) {
                                    return Promise.resolve()
                                  }
                                  return Promise.reject(new Error())
                                },
                                message: '不能为空',
                              },
                            ]}
                          >
                            {(filterType === 'create' || filterType === 'edit') && (
                              <Form.Item
                                name={[field.name, 'value', 'fieldName']}
                                noStyle
                              >
                                <Select
                                  options={optionList.map(option => {
                                    const conditions = form.getFieldValue('conditions') || []
                                    const selectedValues = conditions
                                      .map((item: CombinedFilterItemDataType) => item?.value?.fieldName)
                                    return {
                                      ...option,
                                      disabled: selectedValues.includes(option?.value) && option?.value !== form.getFieldValue('conditions')?.find((item: CombinedFilterItemDataType) => item.id === field.key)?.value?.fieldName,
                                    }
                                  })}
                                  style={{ width: 200 }}
                                  onChange={(v, record: any) => {
                                    setSelectedKeyMap(prevMap => ({
                                      ...prevMap,
                                      [field?.key]: record?.value,
                                    }))
                                    const conditions = form.getFieldValue('conditions') || []
                                    const conditionIndex = conditions.findIndex((item: CombinedFilterItemDataType) => item?.id === field?.key)
                                    if (conditionIndex !== -1) {
                                      // 处理类型切换时组件值残留的问题
                                      const newConditions = [...conditions]
                                      newConditions[conditionIndex] = {
                                        ...newConditions[conditionIndex],
                                        value: {
                                          fieldName: record?.value,
                                          rangeValue: undefined,
                                          label: undefined,
                                        },
                                      }
                                      form.setFieldsValue({ conditions: newConditions })
                                    }
                                  }}
                                />
                              </Form.Item>
                            )}
                            {filterType === 'view' && currentCondition?.value?.fieldName && (
                              <div>
                                {label}
                              </div>
                            )}
                          </Form.Item>

                          {(filterType === 'create' || filterType === 'edit') && (
                            <Form.Item
                              name={[field.name, 'value', 'rangeValue']}
                              style={{ width: 300, marginBottom: 0 }}
                              rules={[
                                {
                                  validator: (_, value) => {
                                    const conditions = form.getFieldValue('conditions') || []
                                    const currentCondition = conditions.find((item: CombinedFilterItemDataType) => item?.id === field?.key)
                                    return validateConditionValue(currentCondition, value, config) ? Promise.resolve() : Promise.reject(new Error())
                                  },
                                  message: '不能为空',
                                },
                              ]}
                            >
                              <RenderComponent
                                selectedKey={selectedKeyMap[field.key]}
                                onChange={(value, option) => {
                                  const conditions = form.getFieldValue('conditions') || []
                                  const conditionIndex = conditions.findIndex((item: CombinedFilterItemDataType) => item?.id === field?.key)
                                  if (conditionIndex !== -1) {
                                    const newConditions = [...conditions]
                                    newConditions[conditionIndex] = {
                                      ...newConditions[conditionIndex],
                                      value: {
                                        ...newConditions[conditionIndex].value,
                                        rangeValue: value,
                                        label: value ? (getItemLabel(newConditions[conditionIndex], value, option) || value || '') : undefined,
                                      },
                                    }
                                    form.setFieldsValue({ conditions: newConditions })
                                  }
                                  handleFormValueChange()
                                }}
                              />
                            </Form.Item>
                          )}

                          {(filterType === 'view') && (
                            <>
                              <div style={{ height: 32, alignSelf: 'center' }}>
                                {opertor}
                              </div>
                              <div style={{ height: 32, alignSelf: 'center' }}>
                                {formatDisplayValue(displayValue, config)}
                              </div>
                            </>
                          )}

                        </div>
                        {fields.length > 1 && filterType !== 'view' && (
                          <Button
                            icon={<CloseCircleOutlined />}
                            type="link"
                            style={{ width: 22, height: 22, color: '#B5BECB', float: 'right' }}
                            onClick={() => {
                              remove(field.name)
                              handleFormValueChange()
                            }}
                          />
                        )}
                      </div>
                    )
                  })}
                  {fields.length > 1 && (
                    <div className="combined-filter-name">
                      且
                    </div>
                  )}
                </div>
              </>
            )
          }}
        </Form.List>
      </Form>
    </div>
  )
}

export default CommonCombinedFilter
